import QRCode from 'qrcode';
import { config } from '@caixa/config';
import {
  imprimirDanfeNfceSimplificado,
  type DanfeNfceSimplificado,
} from '@/lib/cupom';
import type { NfceEmitResponse } from '@caixa/lib/api';
import type { ItemCarrinho, ValoresPagamento } from '@caixa/types';

export const NFC_E_QR_UNAVAILABLE_MSG =
  'QR Code da NFC-e ainda não disponível. Aguarde e tente imprimir novamente.';

export type NfceDanfePrintOpts = {
  docDigits?: string;
  docKind?: '' | 'cpf' | 'cnpj';
  items?: ItemCarrinho[];
  valores?: ValoresPagamento;
};

/** NFC-e autorizada com URL do QR disponível para gerar o cupom simplificado. */
export function nfceProntoParaDanfe(resp: NfceEmitResponse): boolean {
  return resp.authorized === true && Boolean(resp.qrcode_url?.trim());
}

function enrichDestinatario(
  dest: NfceEmitResponse['destinatario'] | undefined,
  docDigits?: string,
  docKind?: '' | 'cpf' | 'cnpj',
): DanfeNfceSimplificado['destinatario'] | undefined {
  const base =
    dest && typeof dest === 'object'
      ? { ...dest }
      : ({} as { cpf?: string; cnpj?: string; nome?: string });
  const cpfApi = (base.cpf ?? '').replace(/\D/g, '');
  const cnpjApi = (base.cnpj ?? '').replace(/\D/g, '');
  if (cpfApi.length === 11 || cnpjApi.length === 14) {
    return base;
  }
  const d = (docDigits ?? '').replace(/\D/g, '');
  if (docKind === 'cpf' && d.length === 11) {
    return { ...base, cpf: d };
  }
  if (docKind === 'cnpj' && d.length === 14) {
    return { ...base, cnpj: d };
  }
  return Object.keys(base).length > 0 ? base : undefined;
}

function itensFromCarrinho(
  items: ItemCarrinho[],
): NonNullable<DanfeNfceSimplificado['itens']> {
  return items.map((i) => ({
    descricao: i.nome,
    quantidade: i.quantidade,
    valor_unitario: i.precoUnitario,
    total: i.quantidade * i.precoUnitario - (i.desconto || 0),
  }));
}

function pagamentosFromValores(
  v: ValoresPagamento,
): NonNullable<DanfeNfceSimplificado['pagamentos']> {
  const out: Array<{ descricao: string; valor: number }> = [];
  if (v.pix > 0) out.push({ descricao: 'PIX', valor: v.pix });
  if (v.dinheiro > 0) out.push({ descricao: 'Dinheiro', valor: v.dinheiro });
  if (v.credito > 0) out.push({ descricao: 'Crédito', valor: v.credito });
  if (v.debito > 0) out.push({ descricao: 'Débito', valor: v.debito });
  return out;
}

export function montarDanfeNfceSimplificado(
  resp: NfceEmitResponse,
  qrcodeDataUrl: string,
  opts?: NfceDanfePrintOpts,
): DanfeNfceSimplificado {
  let itens = Array.isArray(resp.itens) ? resp.itens : [];
  if (itens.length === 0 && opts?.items?.length) {
    itens = itensFromCarrinho(opts.items);
  }

  let pagamentos = Array.isArray(resp.pagamentos) ? resp.pagamentos : [];
  if (pagamentos.length === 0 && opts?.valores) {
    pagamentos = pagamentosFromValores(opts.valores) ?? [];
  }

  const emitenteApi = resp.emitente;
  const emitente =
    emitenteApi && (emitenteApi.nome || emitenteApi.cnpj)
      ? emitenteApi
      : {
          nome: config.empresa.nome,
          cnpj: config.empresa.cnpj,
          endereco: config.empresa.endereco,
          ie: config.empresa.ie,
          logo_url: config.empresa.logo_url,
        };

  return {
    ref: resp.ref,
    chave: normalizeChaveAcesso(resp.chave) ?? extractChaveFromQrcodeUrl(resp.qrcode_url),
    numero: resp.numero,
    serie: resp.serie,
    protocolo: resp.protocolo,
    data_emissao: resp.data_emissao ?? new Date().toISOString(),
    qrcode_url: resp.qrcode_url,
    qrcode_data_url: qrcodeDataUrl,
    url_consulta: resp.url_consulta,
    emitente,
    destinatario: enrichDestinatario(resp.destinatario, opts?.docDigits, opts?.docKind),
    itens,
    pagamentos,
    subtotal: resp.subtotal,
    desconto: resp.desconto,
    total: resp.total,
    valor_total_tributos: resp.valor_total_tributos,
  };
}

function normalizeChaveAcesso(raw?: string | null): string | undefined {
  if (!raw) return undefined;
  let s = raw.trim();
  if (!s) return undefined;
  if (/^NFe/i.test(s)) s = s.slice(3);
  const digits = s.replace(/\D/g, "");
  return digits.length === 44 ? digits : undefined;
}

function extractChaveFromQrcodeUrl(url?: string | null): string | undefined {
  if (!url?.trim()) return undefined;
  const p = url.match(/[?&]p=(\d{44})/i);
  if (p?.[1]) return p[1];
  const any = url.match(/(\d{44})/);
  return any?.[1];
}

export async function gerarQrcodeDataUrl(qrcodeUrl: string): Promise<string> {
  const url = qrcodeUrl.trim();
  if (!url) {
    throw new Error(NFC_E_QR_UNAVAILABLE_MSG);
  }
  return QRCode.toDataURL(url, { margin: 1, width: 256, errorCorrectionLevel: 'M' });
}

/** Imprime DANFE NFC-e simplificado (nunca usa danfe_url / PDF da Focus). */
export async function imprimirNfceDanfe(
  resp: NfceEmitResponse,
  opts?: NfceDanfePrintOpts,
): Promise<void> {
  const qrcodeUrl = resp.qrcode_url?.trim();
  if (!qrcodeUrl) {
    throw new Error(NFC_E_QR_UNAVAILABLE_MSG);
  }
  const qrcodeDataUrl = await gerarQrcodeDataUrl(qrcodeUrl);
  const dados = montarDanfeNfceSimplificado(resp, qrcodeDataUrl, opts);
  await imprimirDanfeNfceSimplificado(dados);
}
